import { createApp } from 'vue'
import './style.scss'
import App from './App.vue'
import router from './router'
import { createPinia } from 'pinia'
import { useAuthStore } from './stores/auth'
import { useTenantStore } from './stores/tenant'
import { initSentry, captureError } from './lib/sentry'
import { initAnalytics } from './lib/analytics'
import { recordTouch } from './lib/attribution'

const app = createApp(App)
const pinia = createPinia()

app.use(pinia)
app.use(router)

// Initialize monitoring (both no-op gracefully when disabled)
initSentry(app, router)
initAnalytics()
recordTouch()

// Global Vue error handler — catches errors that escape components
app.config.errorHandler = (err, instance, info) => {
  captureError(err, {
    componentName: instance?.$options?.name || 'Unknown',
    lifecycleHook: info,
  })
  console.error('[Vue Error]', err, info)
}

// Catch unhandled promise rejections globally
window.addEventListener('unhandledrejection', (event) => {
  captureError(event.reason || new Error('Unhandled promise rejection'), {
    type: 'unhandledrejection',
  })
})

// Bootstrap inside an async IIFE rather than top-level await. Top-level await
// in the entry chunk makes it an async module, and route-level dynamic imports
// (e.g. `() => import('../views/DocsPage.vue')`) that transitively depend on
// the entry chunk deadlock against `router.isReady()` — the chunk waits for
// the entry to finish evaluating, while the entry waits for the chunk to load.
;(async () => {
  // Resolve the tenant subdomain (if any) before auth so login + router
  // guards can gate on tenant membership.
  const tenantStore = useTenantStore()
  await tenantStore.initialize()

  const authStore = useAuthStore()
  await authStore.initialize()

  // Wait for the router's initial navigation (including any redirects from
  // beforeEach guards) to resolve before mounting. Without this, App.vue
  // briefly renders the authenticated AppLayout for protected routes like /
  // before the guard redirects unauthenticated users to /home.
  await router.isReady()

  app.mount('#app')
})()
